import { useRef, useState } from 'preact/hooks';
import { useUser } from '../hooks/UNSAFE_useUser';
import type { ItemActionDetail, ItemInputDetail, ItemInfo } from './PictoChart.type';
import { getItemInfo, isEqualItem } from './PictoChartUtils';
import { getRandomId } from '../utils/PRIVATE_visLayoutUtils/LayoutUtils';

export function useEvents<K>(
  isInteractive: boolean,
  getDetailFromInfo: (info?: ItemInfo) => ItemInputDetail<K>,
  getPrevItemInfo: (info: ItemInfo) => ItemInfo,
  getNextItemInfo: (info: ItemInfo) => ItemInfo,
  onItemAction?: (detail: ItemActionDetail<K>) => void,
  onInput?: (detail: ItemInputDetail<K>) => void
) {
  const [focusedItemInfo, setfocusedItemInfo] = useState<ItemInfo>({ idx: 0 });
  const [hoveredItemInfo, sethoveredItemInfo] = useState<ItemInfo>();

  const activeId = useRef<string>();

  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  const cancelEvent = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const hoverHandler = (event: PointerEvent) => {
    const itemInfo = getItemInfo(event.target as HTMLElement);
    if (!isEqualItem(itemInfo, hoveredItemInfo)) {
      sethoveredItemInfo(itemInfo && { ...itemInfo, isCurrent: true });
      activeId.current = getRandomId();
      if (isInteractive) {
        setfocusedItemInfo({ ...focusedItemInfo, isCurrent: false });
        onInput?.(getDetailFromInfo(itemInfo));
      }
    }
  };

  const pointerLeaveHandler = () => {
    sethoveredItemInfo(undefined);
    activeId.current = undefined;
    if (isInteractive) {
      onInput?.(getDetailFromInfo());
    }
  };

  if (!isInteractive) {
    return {
      focusedItemInfo,
      hoveredItemInfo,
      // include pointer leave and pointer move for datatip
      onPointerLeave: pointerLeaveHandler,
      onPointerMove: hoverHandler
    };
  }

  function updatefocusedItemInfo(itemInfo: ItemInfo) {
    onInput?.(getDetailFromInfo(itemInfo));
    activeId.current = getRandomId();
    setfocusedItemInfo(itemInfo);
  }

  function handleKeyboardInput(item: ItemInfo) {
    if (!isEqualItem(item, focusedItemInfo)) {
      item.isCurrent = true;
      item.isFocusVisible = true;
      if (hoveredItemInfo) {
        sethoveredItemInfo({ ...hoveredItemInfo, isCurrent: false });
      }
      updatefocusedItemInfo(item);
    }
  }

  const triggerOnAction = () => {
    const { id } = getDetailFromInfo(focusedItemInfo);
    if (id != null) {
      onItemAction?.({ id });
      activeId.current = getRandomId();
    }
  };

  const pointerUpHandler = (event: PointerEvent) => {
    const itemInfo = getItemInfo(event.target as HTMLElement);
    if (itemInfo != null) {
      setfocusedItemInfo(itemInfo);
      const { id } = getDetailFromInfo(itemInfo);
      if (id != null) {
        onItemAction?.({ id });
        activeId.current = getRandomId();
      }
    } else onItemAction?.({ id: undefined });
  };

  const keyDownHandler = (event: KeyboardEvent) => {
    const key = event.key;
    switch (key) {
      case 'Tab':
        return;
      case 'ArrowDown': {
        const nextItemInfo = getNextItemInfo(focusedItemInfo);
        handleKeyboardInput(nextItemInfo);
        cancelEvent(event);
        break;
      }
      case 'ArrowUp': {
        const nextItemInfo = getPrevItemInfo(focusedItemInfo);
        handleKeyboardInput(nextItemInfo);
        cancelEvent(event);
        break;
      }
      case 'ArrowRight': {
        const nextItemInfo = isRtl
          ? getPrevItemInfo(focusedItemInfo)
          : getNextItemInfo(focusedItemInfo);
        handleKeyboardInput(nextItemInfo);
        cancelEvent(event);
        break;
      }
      case 'ArrowLeft': {
        const nextItemInfo = isRtl
          ? getNextItemInfo(focusedItemInfo)
          : getPrevItemInfo(focusedItemInfo);
        handleKeyboardInput(nextItemInfo);
        cancelEvent(event);
        break;
      }
    }
  };

  const keyUpHandler = (event: KeyboardEvent) => {
    const key = event.code;
    switch (key) {
      case 'Enter': {
        triggerOnAction();
        cancelEvent(event);
        break;
      }
      case 'Tab': {
        updatefocusedItemInfo({
          ...focusedItemInfo,
          isCurrent: true,
          isFocusVisible: true
        });
        cancelEvent(event);
        break;
      }
    }
  };

  const blurHandler = () => {
    if (hoveredItemInfo != null || focusedItemInfo.isCurrent) {
      onInput?.(getDetailFromInfo(undefined));
    }
    const itemInfo = { ...focusedItemInfo, isCurrent: false, isFocusVisible: false };
    setfocusedItemInfo(itemInfo);
  };

  return {
    focusedItemInfo,
    hoveredItemInfo,
    'aria-activedescendant': activeId.current,
    onPointerUp: pointerUpHandler,
    onPointerMove: hoverHandler,
    onKeyUp: keyUpHandler,
    onKeyDown: keyDownHandler,
    onPointerLeave: pointerLeaveHandler,
    onBlur: blurHandler
  };
}
